import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, Facebook, Instagram, Twitter, Linkedin, Send, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { fileUrl } from "../api";

export default function Footer() {
  const { site } = useSite();
  const { t } = useTranslation();
  return (
    <footer className="bg-[#070d1d] border-t border-white/5 pt-14 pb-6 mt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <img src={site.logoUrl ? fileUrl(site.logoUrl) : "/logo.png"} alt={site.brand} className="h-10 w-10 rounded-xl object-contain" />
              <span className="text-2xl font-extrabold text-white font-[Poppins]">
                {site.brand}<span className="text-[#FFB800]">.guru</span>
              </span>
            </Link>
            <p className="text-slate-400 text-sm mt-4 leading-relaxed">
              {t("footer.aboutText", { users: site.users, since: site.since })}
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a aria-label="Facebook" href={site.facebookUrl} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center text-slate-300 hover:text-[#FFB800] transition-colors"><Facebook className="w-4 h-4" /></a>
              <a aria-label="Instagram" href={site.instagramUrl} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center text-slate-300 hover:text-[#FFB800] transition-colors"><Instagram className="w-4 h-4" /></a>
              <a aria-label="Twitter" href={site.twitterUrl} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center text-slate-300 hover:text-[#FFB800] transition-colors"><Twitter className="w-4 h-4" /></a>
              <a aria-label="Telegram" href={site.telegramUrl} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center text-slate-300 hover:text-[#FFB800] transition-colors"><Send className="w-4 h-4" /></a>
              <a aria-label="LinkedIn" href={site.linkedinUrl} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-amber-500/20 flex items-center justify-center text-slate-300 hover:text-[#FFB800] transition-colors"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-base mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.home")}</Link></li>
              <li><Link to="/cricket-id" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.cricketId")}</Link></li>
              <li><Link to="/how-to-play" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.howToPlay")}</Link></li>
              <li><Link to="/about-us" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.aboutUs")}</Link></li>
              <li><Link to="/blog" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.blog")}</Link></li>
              <li><Link to="/contact-us" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-base mb-4">{t("footer.legal")}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/responsible-gambling" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("footer.responsibleGambling")}</Link></li>
              <li><Link to="/terms-of-service" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("footer.termsOfService")}</Link></li>
              <li><Link to="/privacy-policy" className="text-slate-400 hover:text-[#FFB800] transition-colors">{t("footer.privacyPolicy")}</Link></li>
            </ul>
            <div className="mt-5 flex items-center gap-2 text-xs text-amber-200/70">
              <ShieldCheck className="w-4 h-4 text-amber-400" />
              <span>{t("footer.sslText")}</span>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-base mb-4">{t("footer.getInTouch")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5 text-slate-300"><Phone className="w-4 h-4 text-amber-400 mt-0.5" /><span>{site.phone}</span></li>
              <li className="flex items-start gap-2.5 text-slate-300"><Mail className="w-4 h-4 text-amber-400 mt-0.5" /><a href={`mailto:${site.email}`} className="hover:text-[#FFB800]">{site.email}</a></li>
              <li className="flex items-start gap-2.5 text-slate-300"><MapPin className="w-4 h-4 text-amber-400 mt-0.5" /><span>New Delhi, India</span></li>
            </ul>
            <a href={site.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-block mt-5 px-4 py-2.5 rounded-lg text-sm">{t("footer.waNow")}</a>
          </div>
        </div>

        <div className="glow-divider my-8" />

        <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 sm:p-5 text-[12px] sm:text-xs text-slate-400 leading-relaxed">
          <span className="text-amber-300 font-semibold">{t("footer.disclaimerLabel")}</span> {t("footer.disclaimer", { brand: site.brand })}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-6 text-xs text-slate-500">
          <p>{t("footer.copyright", { year: new Date().getFullYear(), brand: site.brand })}</p>
          <p>{t("footer.poweredBy", { brand: site.brand })}</p>
        </div>
      </div>
    </footer>
  );
}
